import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Settings2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export const readStatsChartPreferences = (storageKey) => {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const storedValue = JSON.parse(
      window.localStorage.getItem(storageKey) ?? "{}",
    );
    return storedValue && typeof storedValue === "object" ? storedValue : {};
  } catch (error) {
    console.error("Error reading statistics chart preferences:", error);
    return {};
  }
};

export const getStatsChartVisibility = (preferences, chart) => {
  try {
    const safePreferences =
      preferences && typeof preferences === "object" ? preferences : {};
    const chartKey = chart?.key;
    if (typeof chartKey !== "string" || chartKey === "") {
      return false;
    }
    const storedVisibility = safePreferences[chartKey]?.visible;
    return typeof storedVisibility === "boolean"
      ? storedVisibility
      : chart?.defaultVisible !== false;
  } catch (error) {
    return chart?.defaultVisible !== false;
  }
};

export const useStatsChartPreferences = (storageKey) => {
  const [preferences, setPreferences] = useState(() =>
    readStatsChartPreferences(storageKey),
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(preferences));
    } catch (error) {
      console.error("Error saving statistics chart preferences:", error);
    }
  }, [preferences, storageKey]);

  const updatePreference = (chartKey, nextPreference) => {
    setPreferences((currentPreferences) => ({
      ...currentPreferences,
      [chartKey]: {
        ...currentPreferences[chartKey],
        ...nextPreference,
      },
    }));
  };

  return { preferences, updatePreference };
};

const ChartSettingRow = ({ chart, preferences, onUpdatePreference }) => {
  const { t } = useTranslation();
  const visible = getStatsChartVisibility(preferences, chart);
  const chartType = preferences[chart.key]?.type === "pie" ? "pie" : "bar";

  return (
    <div
      className="space-y-2 border-b py-3 last:border-b-0"
      data-chart-setting-key={chart.key}
      data-has-data={chart.hasData ? "true" : "false"}
    >
      <div className="flex items-start justify-between gap-3">
        <label className="flex min-w-0 items-start gap-3 text-sm font-medium leading-5">
          <input
            type="checkbox"
            checked={visible}
            onChange={(event) =>
              onUpdatePreference(chart.key, {
                visible: event.target.checked,
              })
            }
            aria-label={`${t("statsPage.questionCharts.showChart")}: ${chart.title}`}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <span className="break-words">{chart.title}</span>
        </label>
        <span
          className="shrink-0 text-xs text-muted-foreground"
          title={
            chart.hasData
              ? t("statsPage.questionCharts.hasData")
              : t("statsPage.questionCharts.noData")
          }
        >
          {chart.hasData
            ? t("statsPage.questionCharts.hasData")
            : t("statsPage.questionCharts.noData")}
        </span>
      </div>
      {chart.supportsType ? (
        <select
          value={chartType}
          onChange={(event) =>
            onUpdatePreference(chart.key, {
              type: event.target.value === "pie" ? "pie" : "bar",
            })
          }
          aria-label={`${t("statsPage.questionCharts.chartType")}: ${chart.title}`}
          className="ml-7 h-9 w-[calc(100%-1.75rem)] rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="bar">{t("statsPage.questionCharts.bar")}</option>
          <option value="pie">{t("statsPage.questionCharts.pie")}</option>
        </select>
      ) : null}
    </div>
  );
};

const ChartSettingsGroup = ({
  title,
  charts,
  preferences,
  onUpdatePreference,
}) => {
  if (charts.length === 0) {
    return null;
  }

  return (
    <section>
      <h3 className="sticky top-0 z-10 border-b bg-background py-2 text-sm font-semibold">
        {title}
      </h3>
      <div>
        {charts.map((chart) => (
          <ChartSettingRow
            key={chart.key}
            chart={chart}
            preferences={preferences}
            onUpdatePreference={onUpdatePreference}
          />
        ))}
      </div>
    </section>
  );
};

const StatsChartSettings = ({ charts, preferences, onUpdatePreference }) => {
  const { t } = useTranslation();
  const standardCharts = charts.filter((chart) => chart.group === "standard");
  const questionCharts = charts.filter((chart) => chart.group === "question");

  return (
    <Sheet>
      <SheetTrigger asChild={true}>
        <Button
          variant="outline"
          size="icon"
          aria-label={t("statsPage.questionCharts.openSettings")}
          title={t("statsPage.questionCharts.openSettings")}
          data-testid="stats-chart-settings-trigger"
        >
          <Settings2 className="h-4 w-4" aria-hidden="true" />
        </Button>
      </SheetTrigger>
      <SheetContent
        className="flex w-full flex-col overflow-hidden sm:max-w-lg"
        data-testid="stats-chart-settings"
      >
        <SheetHeader className="pr-10">
          <SheetTitle>{t("statsPage.questionCharts.settingsTitle")}</SheetTitle>
          <SheetDescription>
            {t("statsPage.questionCharts.settingsDescription")}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4 flex-1 space-y-5 overflow-y-auto pr-2">
          <ChartSettingsGroup
            title={t("statsPage.questionCharts.standardGroup")}
            charts={standardCharts}
            preferences={preferences}
            onUpdatePreference={onUpdatePreference}
          />
          <ChartSettingsGroup
            title={t("statsPage.questionCharts.questionGroup")}
            charts={questionCharts}
            preferences={preferences}
            onUpdatePreference={onUpdatePreference}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default StatsChartSettings;
